<template>
    <div
        v-bind:class="wrapperClasses"
        class="row gx-3 mb-3 card p-3">
        <pc-loader
            v-bind:loading="loading"
            v-bind:message="loadMessage"
            class="col-12">
            <div v-if="showInFlightLegacyEnvelope">
                <pending-envelope-legacy-display v-bind:show-e-signature-status-header="true" />
            </div>
            <div v-else>
                <div class="d-flex align-items-center mb-3">
                    <div class="me-3">
                        <pc-bs-icon
                            name="envelope"
                            class="text-primary"
                            style="font-size: 1.5rem" />
                    </div>
                    <pc-label
                        v-bind:label="label"
                        v-bind:show-required="required"
                        v-bind:show-help="showHelp"
                        v-bind:help-link="showHelpLinkOnly"
                        bold
                        v-on:help-clicked="emitHelpClicked" />
                </div>
                <div class="col-12">
                    <slot name="help" />
                </div>
                <div
                    v-if="!hasEnvelopeDefinition"
                    class="text-danger fw-bold">
                    {{ noEnvelopeDefinitionSelectedError }}
                </div>
                <div v-if="hasEnvelopeDefinition">
                    <div class="col-12">
                        <pc-alert
                            v-bind:severity="statusAlertSeverity"
                            v-bind:shown="showStatus">
                            <envelope-status-text
                                v-bind:text="statusDisplay"
                                v-bind:status-severity="statusSeverity"
                                fill-icon
                                no-text-color
                                bold />
                        </pc-alert>
                    </div>
                    <pc-tabs
                        v-bind:text-capitalize="false"
                        v-bind:hide-single="!showChooseTypeBtn"
                        hide-border>
                        <envelope-item-definition-display-table
                            v-if="!envelopeElement_isStatusCompleted"
                            v-bind:envelope="envelope"
                            v-bind:envelope-definition="envelopeDefinition"
                            class="mb-4" />
                        <div v-if="envelopeElement_isStatusCompleted">
                            <div class="mb-2 fw-bold">
                                {{ savedDocumentsHeader }}
                            </div>
                            <div class="row">
                                <div class="col-12 col-md-6">
                                    <envelope-completed-items
                                        v-bind:envelope="envelope"
                                        v-bind:download-completed-item-fn="viewCompletedEnvelopeAttachment"
                                        class="mb-4" />
                                </div>
                            </div>
                        </div>
                        <pc-tab
                            v-bind:is-shown="showChooseTypeBtn || showESignDisplay"
                            v-bind:title="eSignTab"
                            icon="download"
                            v-on:selected="onSelectESign">
                            <envelope-element-e-sign
                                v-bind:envelope="envelope"
                                v-bind:envelope-definition="envelopeDefinition"
                                v-bind:requestable="!preSubmitError"
                                v-bind:show-notifications-resent-badge="hasResentNotifications"
                                v-on:request-signatures="onRequestSignatures"
                                v-on:fetch-update="onFetchUpdate"
                                v-on:manage-request="onManageRequest"
                                v-on:start-over="onStartOver" />
                        </pc-tab>
                        <pc-tab
                            v-bind:is-shown="showChooseTypeBtn || showWetSignDisplay"
                            v-bind:title="signAndUploadTab"
                            icon="file-earmark-arrow-up"
                            v-on:selected="onSelectWetSign">
                            <envelope-element-wet-sign
                                v-bind:envelope="envelope"
                                v-bind:envelope-definition="envelopeDefinition"
                                v-on:create-wet-sign-envelope="onCreateWetSignEnvelope"
                                v-on:complete-wet-sign-envelope="onCompleteWetSignEnvelope"
                                v-on:void-envelope="onVoidEnvelope"
                                v-on:start-over="onStartOver" />
                        </pc-tab>
                    </pc-tabs>
                </div>
                <validation-render-helper v-bind:validation-error="validationError" />
            </div>
        </pc-loader>
    </div>
</template>

<script>
    import Vuex from "vuex";
    import base, { validationRenderHelper } from "Forms/Components/Elements/_elementBase";
    import { mapI18n } from "Util/i18n";
    import { KEYS as COMMON_KEYS, NAMESPACE as COMMON_NS } from "I18n/common.i18n";
    import { PROGRAM_TYPES } from "SharedStores/Program/programStore";
    import { PROJECT_TYPES } from "SharedStores/Project/projectStore";
    import { PROJECT_FORM_TYPES } from "Forms/Components/ProjectForm/projectFormStore";
    import { PROJECT_FORM_CONTROLLER_TYPES } from "Forms/Components/ProjectForm/projectFormControllerStore";

    import { envelopeElementMixin } from "./envelopeElementMixin";
    import envelopeItemDefinitionDisplayTable from "./envelopeItemDefinitionDisplayTable.vue";
    import envelopeElementESign from "./envelopeElementESign.vue";
    import envelopeElementWetSign from "./envelopeElementWetSign.vue";
    import envelopeCompletedItems from "./envelopeCompletedItems.vue";
    import manageRequestDialog from "./manageRequestDialog.vue";
    import startOverEnvelopeDialog from "./startOverEnvelopeDialog.vue";
    import envelopeStatusText from "./envelopeStatusText.vue";
    import pendingEnvelopeLegacyDisplay from "ESignatures/Legacy/pendingEnvelopeLegacyDisplay.vue";

    import {
        ESIGNATURE_STATUS_SEVERITY,
        getEnvelopeStatusAlertSeverity,
        getEnvelopeStatusSeverity,
        WET_SIGN_VOID_REASON
    } from "ESignatures/eSignatureConstants";
    import { ENVELOPE_ITEM_TYPE } from "ESignatures/EditEnvelope/editEnvelopeStore";
    import { ESIGNATURE_CANCEL_EXPLANATION_TYPE } from "ESignatures/eSignatureConstants";

    import { DIALOG_RESULT } from "Components/pc-dialog.vue";
    import { ALERT_SEVERITY } from "Util/alerts";

    const SIGNATURE_TYPE_OPTION = {
        ESign: 1,
        WetSign: 2
    };

    export default {
        components: {
            validationRenderHelper,
            envelopeElementESign,
            envelopeElementWetSign,
            envelopeItemDefinitionDisplayTable,
            envelopeCompletedItems,
            envelopeStatusText,
            pendingEnvelopeLegacyDisplay
        },
        extends: base,
        mixins: [envelopeElementMixin],
        inject: {
            viewport: {
                from: "viewport",
                default: null
            }
        },
        provide() {
            return {
                readonly: this.readonly,
                isProjectFormControllerModuleRegistered: this.isProjectFormControllerModuleRegistered()
            };
        },
        data() {
            return {
                loading: false,
                loadMessage: null,
                selectedTabSignatureType: SIGNATURE_TYPE_OPTION.ESign, // default: check if wet sign in mounted
                hasResentNotifications: false
            };
        },
        computed: {
            showStatus() {
                return Boolean(this.envelope) || this.preSubmitError;
            },
            statusDisplay() {
                if (!this.showStatus) {
                    return "";
                }
                if (this.preSubmitError) {
                    if (this.missingRequiredSignatory && this.envelopeElement_missingRequiredAttachment) {
                        return this.recipientsAreIncompleteAndMissingAttachmentsError;
                    } else if (this.envelopeElement_missingRequiredAttachment) {
                        return this.missingAttachmentsError;
                    }
                    return this.recipientsAreIncompleteError;
                }
                if (this.showWetSignDisplay && this.envelopeElement_isStatusNew) {
                    let allComplete = true;
                    for (const itemDefinition of this.envelopeElement_orderedItemDefinitions.filter((i) =>
                        this.itemIsDocumentTemplate(i)
                    )) {
                        if (
                            !this.envelopeElement_orderedItems.find((i) => i.DefinitionId === itemDefinition.PublicId)
                                ?.Document
                        ) {
                            allComplete = false;
                            break;
                        }
                    }
                    if (allComplete) {
                        return this.documentsAreCompleteAlert;
                    }
                }
                const statusText = this.envelope.Status.Display;
                const statusTimestamp =
                    this.envelope.StatusTimestamp.Ticks === 0
                        ? this.envelope.CreatedTimestamp.Display
                        : this.envelope.StatusTimestamp.Display;
                if (statusTimestamp) {
                    return `${statusText}: ${statusTimestamp}`;
                } else {
                    return statusText;
                }
            },
            statusAlertSeverity() {
                if (this.preSubmitError) {
                    return ALERT_SEVERITY.error;
                }
                return getEnvelopeStatusAlertSeverity(this.envelope?.Status.Code);
            },
            statusSeverity() {
                if (this.preSubmitError) {
                    return ESIGNATURE_STATUS_SEVERITY.Error;
                }
                return getEnvelopeStatusSeverity(this.envelope?.Status.Code);
            },
            showChooseTypeBtn() {
                return (
                    this.envelopeDefinition &&
                    this.envelopeDefinition.AllowsWetSign &&
                    !this.envelopeElement_hasEnvelope
                );
            },
            preSubmitError() {
                return (
                    !this.envelope && (this.missingRequiredSignatory || this.envelopeElement_missingRequiredAttachment)
                );
            },
            missingRequiredSignatory() {
                return !this.envelopeElement_recipientContactInfoComplete && this.eSignTabSelected;
            },
            eSignTabSelected() {
                return this.selectedTabSignatureType === SIGNATURE_TYPE_OPTION.ESign;
            },
            showESignDisplay() {
                return this.signatureType === SIGNATURE_TYPE_OPTION.ESign;
            },
            showWetSignDisplay() {
                return this.signatureType === SIGNATURE_TYPE_OPTION.WetSign;
            },
            projectClientDocumentStatuses() {
                if (this.isProjectFormModuleRegistered()) {
                    return this.classicESignatureDocumentStatuses;
                }
                return [];
            },
            showInFlightLegacyEnvelope() {
                return this.projectClientDocumentStatuses && this.projectClientDocumentStatuses.length > 0;
            },
            envelope() {
                if (!this.isProjectModuleRegistered()) {
                    return null;
                }
                return this.getEnvelopeFromDefinitionId(this.element.EnvelopeId);
            },
            readonly() {
                return this.baseReadonly || this.isPrefill;
            },
            /**
             * For an active envelope, the definition is embedded in the ClientEnvelope
             * object because we only store the current definition version in the program
             * store, and the active envelope may have been created with a previous version.
             * Either way, the envelopeDefinition will be an instance of  ClientEnvelopeDefinition
             */
            envelopeDefinition() {
                return (
                    (this.envelope && this.envelope.Definition) ||
                    this.getEnvelopeDefinitionFromId(this.element.EnvelopeId)
                );
            },
            hasEnvelopeDefinition() {
                return Boolean(this.envelopeDefinition);
            },
            signatureType() {
                if (this.envelope) {
                    if (this.envelope.IsWetSign) {
                        return SIGNATURE_TYPE_OPTION.WetSign;
                    } else {
                        return SIGNATURE_TYPE_OPTION.ESign;
                    }
                } else if (this.envelopeDefinition && !this.envelopeDefinition.AllowsWetSign) {
                    return SIGNATURE_TYPE_OPTION.ESign;
                }
                return null;
            },
            wrapperClasses() {
                return {
                    "shadow-sm": this.isProjectFormModuleRegistered() && !this.isAdmin
                };
            },
            ...Vuex.mapGetters({
                getEnvelopeDefinitionFromId: PROGRAM_TYPES.getters.getEnvelopeDefinitionFromId,
                getEnvelopeFromDefinitionId: PROJECT_TYPES.getters.getEnvelopeFromDefinitionId,
                classicESignatureDocumentStatuses: PROJECT_FORM_TYPES.getters.classicESignatureDocumentStatuses,
                isAdmin: PROJECT_FORM_TYPES.getters.isAdmin,
                getUserLoginIdToUserInfo: PROJECT_FORM_TYPES.getters.getUserLoginIdToUserInfo,
                dataFieldValueFromId: PROJECT_TYPES.getters.dataFieldValueFromId
            }),
            ...mapI18n({ ...COMMON_KEYS }, COMMON_NS)
        },
        mounted() {
            if (this.envelope?.IsWetSign) {
                this.selectedTabSignatureType = SIGNATURE_TYPE_OPTION.WetSign;
            }
        },
        methods: {
            onSelectESign() {
                this.selectedTabSignatureType = SIGNATURE_TYPE_OPTION.ESign;
            },
            onSelectWetSign() {
                this.selectedTabSignatureType = SIGNATURE_TYPE_OPTION.WetSign;
            },
            onRequestSignatures() {
                this._requestSignatures();
            },
            async onVoidEnvelope() {
                if (this.readonly) {
                    return;
                }

                if (
                    await this.$app.showConfirmationDialog(
                        this.discardEnvelopeModalHeader,
                        this.discardEnvelopeModalContent
                    )
                ) {
                    const voidData = {
                        // not a localized string - this is just explanation text for the back end
                        explanationType: ESIGNATURE_CANCEL_EXPLANATION_TYPE.WetSign,
                        explanationDetail: WET_SIGN_VOID_REASON
                    };
                    this._voidSignatures(voidData);
                }
            },
            onFetchUpdate() {
                this._fetchUpdate();
            },
            onCreateWetSignEnvelope() {
                this._createWetSignEnvelope();
            },
            onCompleteWetSignEnvelope() {
                this._completeWetSignEnvelope();
            },
            async onStartOver() {
                let result = DIALOG_RESULT.ok;
                if (this.envelopeElement_isStatusCompleted) {
                    result = await this.$app.showDialog({
                        title: this.hideEnvelopeConfirmationHeader,
                        customComponent: startOverEnvelopeDialog,
                        customComponentOptions: {
                            envelope: this.envelope
                        },
                        buttons: [
                            {
                                text: this.cancelText,
                                result: DIALOG_RESULT.cancel
                            },
                            {
                                text: this.startOverButton,
                                primary: true,
                                result: DIALOG_RESULT.ok
                            }
                        ]
                    });
                }

                if (result === DIALOG_RESULT.ok) {
                    this.loading = true;
                    this.loadMessage = this.startingOverLoadingText;
                    try {
                        await this.startOverEnvelope({ envelopeId: this.envelope.EnvelopeId });
                    } finally {
                        this.loading = false;
                    }
                }
            },
            async onManageRequest() {
                let resend = false;
                await this.$app.showDialog({
                    large: true,
                    title: this.manageRequestButton,
                    customComponent: manageRequestDialog,
                    customComponentOptions: {
                        envelope: this.envelope,
                        providerCapabilities: this.$serviceControllers.Envelopes.ESignatureProviderCapabilities,
                        getDataFieldValueByIdFn: this.dataFieldValueFromId,
                        getUserInfoByLoginIdFn: this.getUserLoginIdToUserInfo
                    },
                    customComponentListeners: {
                        "cancel-all": this._voidSignatures,
                        "resend-notifications": () => {
                            this.$app.closeDialog();
                            resend = true;
                        }
                    },
                    buttons: [
                        {
                            text: this.closeDialogButton,
                            primary: true,
                            result: DIALOG_RESULT.cancel
                        }
                    ]
                });

                // sort of a hack - resend is called in a modal and opens a modal to confirm saving form
                // (in the event that this is the admin page)
                if (resend) {
                    await this._resendNotifications();
                }
            },
            async _resendNotifications() {
                if (this.loading) {
                    return;
                }

                try {
                    this.loading = true;
                    this.loadMessage = this.resendingNotificationsLoadingText;

                    const envelopeId = this.envelope.EnvelopeId;

                    await this.saveFormData();

                    await this.resendESignatureNotificationsV2({
                        envelopeId: envelopeId
                    });
                    this.hasResentNotifications = true;
                } finally {
                    this.loading = false;
                }
            },
            async _requestSignatures() {
                if (this.loading || !this.isProjectFormModuleRegistered()) {
                    return;
                }

                try {
                    this.loading = true;
                    this.loadMessage = this.requestingSignaturesLoadingText;

                    await this.requestSignaturesV2({
                        envelopeDefinitionId: this.element.EnvelopeId
                    });
                } finally {
                    this.loading = false;
                }
            },
            async _voidSignatures(voidData) {
                if (this.loading) {
                    return;
                }

                try {
                    this.$app.closeDialog();
                    this.loading = true;
                    this.loadMessage = this.envelope.IsWetSign
                        ? this.discardingEnvelopeLoadingText
                        : this.voidingSignaturesLoadingText;

                    await this.voidSignaturesV2({
                        envelopeId: this.envelope.EnvelopeId,
                        ...voidData
                    });
                } finally {
                    this.loading = false;
                }
            },
            async _fetchUpdate() {
                if (this.loading || !this.envelopeElement_hasEnvelope) {
                    return;
                }

                try {
                    this.loading = true;
                    // cpr only - no need to localize
                    this.loadMessage = "Loading...";

                    await this.fetchEnvelopeUpdate({
                        envelopeId: this.envelope.EnvelopeId
                    });
                } finally {
                    this.loading = false;
                }
            },
            async _createWetSignEnvelope() {
                if (this.loading || !this.isProjectFormModuleRegistered()) {
                    return;
                }

                try {
                    this.loading = true;
                    this.loadMessage = this.creatingLoadingText;
                    await this.createWetSignEnvelope({
                        envelopeDefinitionId: this.element.EnvelopeId
                    });
                } finally {
                    this.loading = false;
                }
            },
            async _completeWetSignEnvelope() {
                if (this.loading) {
                    return;
                }

                try {
                    this.loading = true;
                    this.loadMessage = this.completingEnvelopeLoadingText;
                    await this.completeWetSignEnvelope({ envelopeId: this.envelope.EnvelopeId });
                } finally {
                    this.loading = false;
                }
            },
            itemIsDocumentTemplate(itemDefinition) {
                return itemDefinition.Type === ENVELOPE_ITEM_TYPE.DocumentTemplate;
            },
            ...Vuex.mapActions({
                requestSignaturesV2: PROJECT_FORM_TYPES.actions.requestSignaturesV2,
                resendESignatureNotificationsV2: PROJECT_FORM_TYPES.actions.resendESignatureNotificationsV2,
                voidSignaturesV2: PROJECT_FORM_TYPES.actions.voidSignaturesV2,
                fetchEnvelopeUpdate: PROJECT_FORM_TYPES.actions.fetchEnvelopeUpdate,

                createWetSignEnvelope: PROJECT_FORM_TYPES.actions.createWetSignEnvelope,
                completeWetSignEnvelope: PROJECT_FORM_TYPES.actions.completeWetSignEnvelope,
                startOverEnvelope: PROJECT_FORM_TYPES.actions.startOverEnvelope,
                viewCompletedEnvelopeAttachment: PROJECT_FORM_CONTROLLER_TYPES.actions.viewCompletedEnvelopeAttachment
            })
        }
    };
</script>

<style scoped>
    :deep(td) {
        vertical-align: middle;
    }
</style>
